import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { T } from "@/lib/testIds";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    business_name: "",
    business_type: "collector",
    contact_number: "",
    city: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signup(form);
      toast.success("Workspace created — welcome to CollectOne");
      nav("/app/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMG9mZmljZSUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3ODU1Njc4MDZ8MA&ixlib=rb-4.1.0&q=85"
          alt="workspace"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="co-label" style={{ color: "rgba(255,255,255,0.75)" }}>
            Setup takes 30 seconds
          </div>
          <div className="font-heading text-3xl font-bold tracking-tight mt-3 max-w-md">
            Bring every subscription reminder into one place.
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12">
        <div className="max-w-md w-full">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <img src="/collectone-logo.webp" alt="CollectOne" className="w-8 h-8 object-contain" />
            <span className="font-heading font-bold tracking-tight">CollectOne</span>
          </Link>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Create workspace
          </h1>
          <p className="mt-2 text-sm text-[#52525B]">
            <span className="inline-block bg-[#F0FDF4] border border-[#BBF7D0] text-[#15803D] px-2 py-0.5 rounded-sm text-xs font-semibold mr-2">
              3-day free trial
            </span>
            No card required. Access ends automatically after 3 days.
          </p>

          <form onSubmit={submit} data-testid={T.signupForm} className="mt-8 space-y-5">
            <div>
              <Label className="co-label">Your name</Label>
              <Input
                data-testid={T.signupName}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                className="mt-2 h-11"
                placeholder="Aditi Sharma"
              />
            </div>
            <div>
              <Label className="co-label">Business name</Label>
              <Input
                data-testid={T.signupBiz}
                value={form.business_name}
                onChange={(e) => set("business_name", e.target.value)}
                required
                className="mt-2 h-11"
                placeholder="Sharma Coaching Classes"
              />
            </div>
            <div>
              <Label className="co-label">Email</Label>
              <Input
                data-testid={T.signupEmail}
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
                className="mt-2 h-11"
                placeholder="you@business.com"
              />
            </div>
            <div>
              <Label className="co-label">Password</Label>
              <Input
                data-testid={T.signupPassword}
                type="password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                required
                minLength={6}
                className="mt-2 h-11"
                placeholder="At least 6 characters"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="co-label">Contact number</Label>
                <Input
                  data-testid={T.signupContact}
                  value={form.contact_number}
                  onChange={(e) => set("contact_number", e.target.value)}
                  className="mt-2 h-11"
                  placeholder="+91 98xxxxxx00"
                />
              </div>
              <div>
                <Label className="co-label">City</Label>
                <Input
                  data-testid={T.signupCity}
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  className="mt-2 h-11"
                  placeholder="Mumbai"
                />
              </div>
            </div>

            <div>
              <Label className="co-label">Business type</Label>
              <RadioGroup
                value={form.business_type}
                onValueChange={(v) => set("business_type", v)}
                className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
                data-testid={T.signupType}
              >
                {[
                  { v: "collector", t: "Payment collector", d: "Coaching, SaaS, retail, gym" },
                  { v: "distributor", t: "Financial distributor", d: "Insurance agent, MF distributor" },
                ].map((o) => (
                  <label
                    key={o.v}
                    className={`border co-hairline rounded-sm p-4 cursor-pointer transition-colors duration-200 ${
                      form.business_type === o.v ? "border-[#4338CA] bg-[#ECECFE]" : "hover:bg-[#FAFAFA]"
                    }`}
                    data-testid={`signup-type-${o.v}`}
                  >
                    <div className="flex items-start gap-2">
                      <RadioGroupItem value={o.v} className="mt-1" />
                      <div>
                        <div className="font-semibold text-sm">{o.t}</div>
                        <div className="text-xs text-[#52525B] mt-0.5">{o.d}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <button
              type="submit"
              data-testid={T.signupSubmit}
              disabled={busy}
              className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white font-semibold py-3 rounded-sm co-btn-press transition-colors duration-200 disabled:opacity-60"
            >
              {busy ? "Creating…" : "Start free trial (3 days)"}
            </button>
          </form>

          <div className="mt-6 text-sm text-[#52525B]">
            Already have a workspace?{" "}
            <Link to="/login" className="text-[#4338CA] font-semibold hover:text-[#3730A3]">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
